import api from './api.service';

export interface RatePayload {
    iso2Origin: string;
    iso2Destination: string;
    serviceType: string;
    height: number;
    width: number;
    length: number;
    weight: number;
    parcelOrdDoc: string;
    currency: string;
    unitSystem: string;
    fob: number;
    zipcodeOrigin: string;
    zipcodeDestination: string;
    addressLineOrigin?: string;
    cityOrigin?: string;
    stateOrigin?: string;
    addressLineDestination?: string;
    stateDestiny?: string;
    municipalityDestiny?: string;
    pickupDate?: string;
    externalCarrierName?: string; // New property
}

export interface CalculateExternalRatePayload {
    idCarrier: number;
    iso2Origin: string;
    iso2Destination?: string;
    height: number;
    width: number;
    length: number;
    weight: number;
    unitSystem: string;
    zipcodeOrigin: string;
    addressLineOrigin?: string;
    cityOrigin?: string;
    stateOrigin?: string;
    pickupDate?: string;
}

export interface ExternalRateServiceOption {
    serviceName: string;
    serviceQty: number;
    servicePrice: number;
    serviceSubTotal: number;
    idExternalCourierService: number;
    transitDays: string | null;
    estimatedDeliveryDate?: string | null;
    standardPrice?: number | null;
    discountPercent?: number | null;
}

export interface ExternalRateProviderResponse {
    providerName: string;
    idExternalCourier: number;
    options: ExternalRateServiceOption[];
    pickUpService: ExternalRateServiceOption | null;
    elpaketDispatcherService: ExternalRateServiceOption | null;
}

interface CalculateExternalRateApiResponse {
    statusCode: number;
    message: string;
    data: ExternalRateProviderResponse[];
}

export interface RateMultiPayload extends Omit<RatePayload, 'weight'> {
    weights: number[];
}

export interface RateMultiResponse {
    weight: number;
    rates: RateOption[];
}

export interface ServiceDetail {
    serviceName: string;
    serviceQty: number;
    servicePrice: number;
    serviceSubTotal: number;
    mandatory?: boolean;
    idCarrierService?: number | null;
    idExternalCourierService?: number | null;
    transitDays?: string | null;
    estimatedDeliveryDate?: string | null;
    iso3Currency?: string | null;
    standardPrice?: number | null;
    discountPercent?: number | null;
}

export interface ExternalProviderResponse {
    providerName: string;
    idExternalCourier?: number;
    isRequired?: boolean;
    options: ServiceDetail[];
    pickUpService?: ServiceDetail | null;
    elpaketDispatcherService?: ServiceDetail | null;
}

export interface RateOption {
    carrierName: string;
    idCarrier?: number;
    idCarrierService?: number | null;
    rate: number;
    standardRate?: number | null;
    discountPercent?: number | null;
    currency: string;
    serviceIndicator?: string | null;
    services: ServiceDetail[];
    externalServices?: ExternalProviderResponse[];
    estimatedMinDays?: number | null;
    estimatedMaxDays?: number | null;
}

interface RateResponse {
    statusCode: number;
    message: string;
    data: RateOption[];
}

interface RateMultiApiResponse {
    statusCode: number;
    message: string;
    data: RateMultiResponse[];
}

class RateService {
    async calculateRate(payload: RatePayload): Promise<RateOption[]> {
        try {
            const response = await api.post<RateResponse>('/calculate-rate', payload);
            // Validamos si la respuesta trae data
            if (response.data && response.data.data) {
                return response.data.data;
            }
            return [];
        } catch (error) {
            console.error('Error calculating rates:', error);
            throw error;
        }
    }

    async calculateExternalRate(payload: CalculateExternalRatePayload): Promise<ExternalRateProviderResponse[]> {
        try {
            const response = await api.post<CalculateExternalRateApiResponse>('/calculate-external-rate', payload);
            if (response.data && response.data.data) {
                return response.data.data;
            }
            return [];
        } catch (error) {
            console.error('Error calculating external rates:', error);
            throw error;
        }
    }

    async calculateRateMulti(payload: RateMultiPayload): Promise<RateMultiResponse[]> {
        try {
            const response = await api.post<RateMultiApiResponse>('/calculate-rate-multi', payload, {
                headers: { 'X-Skip-Loading': 'true' }
            });
            if (response.data && response.data.data) {
                return response.data.data;
            }
            return [];
        } catch (error) {
            console.error('Error calculating multi rates:', error);
            throw error;
        }
    }

    getInitialExternalIndices(rate: RateOption): Set<number> {
        const initial = new Set<number>();
        if (!rate.externalServices) return initial;

        let globalIdx = 0;
        rate.externalServices.forEach(provider => {
            if (provider.isRequired && provider.options.length > 0) {
                const mandatoryIdx = provider.options.findIndex(o => o.mandatory);
                if (mandatoryIdx !== -1) {
                    initial.add(globalIdx + mandatoryIdx);
                } else {
                    // Fallback to the first option if none is marked as mandatory but the group IS required
                    initial.add(globalIdx);
                }
            }
            globalIdx += provider.options.length;
        });
        return initial;
    }

    toggleExternalService(rate: RateOption, current: Set<number>, clickedIndex: number): Set<number> {
        if (!rate.externalServices) return current;

        const next = new Set(current);
        let globalIdx = 0;

        for (const provider of rate.externalServices) {
            const groupIndices = provider.options.map((_, i) => globalIdx + i);
            const isClickedInGroup = groupIndices.includes(clickedIndex);

            if (isClickedInGroup) {
                const isCurrentlySelected = current.has(clickedIndex);

                if (isCurrentlySelected) {
                    // Only allow toggle off if the group is NOT required
                    if (!provider.isRequired) {
                        next.delete(clickedIndex);
                    }
                } else {
                    // Turn on and turn off others in the same group (mutual exclusion)
                    groupIndices.forEach(idx => next.delete(idx));
                    next.add(clickedIndex);
                }
                break;
            }
            globalIdx += provider.options.length;
        }

        return next;
    }

    calculateTotal(rate: RateOption, selectedExternalIndices: Set<number>): number {
        const internalTotal = rate.services.reduce((acc, s) => acc + s.serviceSubTotal, 0);
        let externalTotal = 0;

        if (rate.externalServices) {
            let globalIdx = 0;
            rate.externalServices.forEach(provider => {
                provider.options.forEach(option => {
                    if (selectedExternalIndices.has(globalIdx)) {
                        externalTotal += option.serviceSubTotal;
                    }
                    globalIdx++;
                });
            });
        }

        return internalTotal + externalTotal;
    }

    /**
     * Ordena los couriers por su precio propio (sin servicios externos), del más barato al más caro.
     * Mantiene el orden original de los sub-servicios de cada courier.
     */
    sortByCheapest(results: RateOption[]): RateOption[] {
        const sorted = [...results].map(rate => ({ ...rate }));

        const courierPrice = (r: RateOption) =>
            r.rate || r.services.reduce((acc, s) => acc + s.serviceSubTotal, 0);
        return sorted.sort((a, b) => courierPrice(a) - courierPrice(b));
    }
}

export const rateService = new RateService();
